/* eslint-disable no-unused-expressions */
/**
 * Custom element that generates stylable select
 *
 * Usage:
 *     <select-styler>
 *         <select>
 *             <option value="">Please select an option</option>
 *             <option value="1">Option 1</option>
 *             <option value="2">Option 2</option>
 *             ...
 *         </select>
 *     </select-styler>
 */
class SelectStyler extends HTMLElement {

    selectEl;
    detailsEl;
    comboboxEl;
    listboxEl;

    optionMap = new Map();
    listItemMap = new Map();

    connectedCallback() {
        // Get select element
        this.selectEl = this.querySelector('select');
        if (this.selectEl) {
            this.selectEl.hidden = true;
        }
        if (!this.selectEl) {
            return;
        }
        const selectElAriaLabel = this.selectEl.getAttribute('aria-label') || '';
        const listboxId = `select-styler-listbox-${(SelectStyler.instanceCount = (SelectStyler.instanceCount || 0) + 1)}`;

        // Create elements
        this.detailsEl = this.createElement('details');
        this.comboboxEl = this.createElement('summary', {
            role: 'combobox', 'aria-label': selectElAriaLabel, 'aria-expanded': 'false', 'aria-controls': listboxId,
        });
        this.comboboxSpanEl = this.createElement('span');
        this.listboxEl = this.createElement('ul', { role: 'listbox', id: listboxId });

        // Append elements
        this.comboboxEl.append(this.comboboxSpanEl);
        this.detailsEl.append(this.comboboxEl, this.listboxEl);
        this.append(this.detailsEl);

        // Get options and create option elements
        [...this.selectEl.options].forEach((option) => this.addOption(option));

        // Complete setup
        this.addEventListeners();
        this.addMutationObservers();
        this.syncSelected();
        this.selectEl.sync = this.syncSelected; // If the value needs to be changed via javascript
    }

    /**
     * DOM interface methods
     */
    addMutationObservers = () => {
        this.selectElObserver = new MutationObserver((mutationList) => {
            mutationList.forEach((mutation) => {
                // Update styler when options are added/removed from selectEl
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((addedNode) => {
                        this.addOption(addedNode, mutation.nextSibling);
                    });
                    mutation.removedNodes.forEach(this.removeOption);

                } else if (mutation.type === 'attributes') {
                    // Mirror data attributes to listItems
                    if (mutation.attributeName.startsWith('data-')) {
                        const listItemEl = this.optionMap.get(mutation.target);
                        listItemEl.setAttribute(mutation.attributeName, mutation.target.getAttribute(mutation.attributeName));

                    // Update styler when options are enabled/disabled
                    } else if (mutation.attributeName === 'disabled') {
                        const listItemEl = this.optionMap.get(mutation.target);
                        listItemEl.setAttribute('aria-disabled', mutation.target.disabled);
                    }
                }
            });
        }).observe(this.selectEl, { subtree: true, childList: true, attributes: true });
    };

    addOption = (optionEl, referenceNode = null) => {
        const listItemEl = this.createElement('li', {
            role: 'option',
            tabindex: -1,
        });
        listItemEl.textContent = optionEl.textContent;

        this.optionMap.set(optionEl, listItemEl);
        this.listItemMap.set(listItemEl, optionEl);
        this.listboxEl.insertBefore(listItemEl, referenceNode && this.optionMap.get(referenceNode));
    };

    removeOption = (optionEl) => {
        const listItemEl = this.optionMap.get(optionEl);
        this.optionMap.delete(optionEl);
        this.listItemMap.delete(listItemEl);
        listItemEl.remove();
    };

    /**
     * UI interaction
     */
    addEventListeners = () => {
        // Close on focusout
        this.addEventListener('focusout', (ev) => {
            if (!this.contains(ev.relatedTarget)) {
                this.detailsEl.open = false;
            }
        });

        // Focus selected option on open
        this.detailsEl.addEventListener('toggle', () => {
            this.comboboxEl.setAttribute('aria-expanded', this.detailsEl.open);
            if (this.detailsEl.open) {
                this.listboxEl.querySelector('[aria-selected="true"]').focus();
            }
        });

        // Open listbox with ArrowDown
        this.comboboxEl.addEventListener('keydown', (ev) => {
            if (ev.key === 'ArrowDown') {
                ev.preventDefault();
                this.detailsEl.open = true;
            }
        });

        // Select option
        this.listboxEl.addEventListener('click', (ev) => {
            if (ev.target.getAttribute('role') === 'option') {
                this.selectOption(ev.target);
            }
        });

        // Navigate listbox
        this.listboxEl.addEventListener('keydown', (ev) => {
            let isHandled = true;
            switch (ev.key) {
            case 'Enter':
            case ' ':
                this.selectOption(ev.target);
                break;
            case 'Escape':
                this.detailsEl.open = false;
                this.comboboxEl.focus();
                break;
            case 'ArrowDown':
                ev.target.nextElementSibling?.focus();
                break;
            case 'ArrowUp':
                ev.target.previousElementSibling?.focus();
                break;
            default:
                isHandled = false;
                break;
            }
            if (isHandled) {
                ev.preventDefault();
            }
        });

        // Handle reset if part of a form
        this.selectEl.form?.addEventListener('reset', () => {
            window.setTimeout(this.syncSelected); // Timeout is needed to wait for reset to complete
        });
    };

    selectOption = (listItemEl) => {
        if (listItemEl.getAttribute('aria-disabled') === 'true') {
            return;
        }
        if (listItemEl.getAttribute('aria-selected') !== 'true') {
            this.selectEl.value = this.listItemMap.get(listItemEl).value;
            this.syncSelected();
            this.selectEl.dispatchEvent(new Event('change'));
        }
        this.detailsEl.open = false;
        this.comboboxEl.focus();
    };

    syncSelected = () => {
        const selectedOption = this.selectEl.selectedOptions[0];
        const selectedListItem = this.optionMap.get(selectedOption);
        if (selectedOption && selectedListItem) {
            this.comboboxSpanEl.textContent = selectedOption.textContent;
            this.listboxEl.childNodes.forEach((optionEl) => {
                optionEl.setAttribute('aria-selected', false);
            });
            selectedListItem.setAttribute('aria-selected', true);
        }
    };

    /**
     * Helpers
     */
    createElement = (tagName, attributes) => {
        const element = document.createElement(tagName);
        if (attributes) {
            Object.entries(attributes).forEach(([key, value]) => {
                if (key != null && value != null) {
                    element.setAttribute(key, value);
                }
            });
        }
        return element;
    };

}

window.customElements.define('select-styler', SelectStyler);
